import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LiveContext = createContext({ unread: 0, lastEvent: null, connected: false, bump: () => {} });

function syncAppBadge(count) {
  if (!("setAppBadge" in navigator)) return;
  const p = count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
  p.catch(() => {});
}

function openStream(onHello, onNotification, onError) {
  const es = new EventSource(`${API}/live`, { withCredentials: true });
  es.addEventListener("hello", onHello);
  es.addEventListener("notification", onNotification);
  es.onerror = onError;
  return es;
}

export function LiveProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const [connected, setConnected] = useState(false);

  const handleNotification = useCallback((e) => {
    const n = JSON.parse(e.data);
    setUnread((u) => u + 1);
    setLastEvent({ ...n, receivedAt: Date.now() });
    toast(n.title, { description: n.body, id: n.id });
  }, []);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, [user]);

  useEffect(() => { syncAppBadge(user ? unread : 0); }, [unread, user]);

  useEffect(() => {
    if (!user) { setConnected(false); return undefined; }
    let es;
    let retry;
    let delay = 5000;
    const connect = () => {
      es = openStream(() => { setConnected(true); delay = 5000; }, handleNotification, () => {
        setConnected(false); es.close(); retry = setTimeout(connect, delay); delay = Math.min(delay * 2, 60000);
      });
    };
    connect();
    return () => { clearTimeout(retry); if (es) es.close(); };
  }, [user, handleNotification]);

  const bump = useCallback((fn) => setUnread(fn), []);
  const value = useMemo(() => ({ unread, lastEvent, connected, bump }), [unread, lastEvent, connected, bump]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);
